import {
  DEFAULT_ROWS_PER_PAGE,
  TableCellType,
  TableSortType,
  LazyTable,
} from "@knowfully/ui.common";
import DateTimeCell from "./DateTimeCell";
import { Button } from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import { useQuery } from "react-query";
import {
  CatalogFilter,
  ListRequestOfCatalogFilter,
  LiveOnlineCourseModel,
} from "services/organizationPortalClient";
import useGlobalContext from "hooks/globalData";
import { useEffect, useState } from "react";
import { useIntl } from "react-intl";
import CreditsCell from "pages/CourseCatalog/CreditsCell";
import { CourseTitleCell } from "components/CourseTitleCell/CourseTitleCell";
import { Link } from "react-router-dom";
import contextStore from "stores/contextStore";

type ListViewProps = {
  filter: CatalogFilter;
  viewSwitch?: React.ReactNode;
};

export default function ListView({ filter, viewSwitch }: ListViewProps) {
  const { formatMessage: t } = useIntl();
  const { catalogService, courseDetailsEnrollUsersUrl } = useGlobalContext();
  const [order, setOrder] = useState<TableSortType>("asc");
  const [orderBy, setOrderBy] = useState("eventStartDate");
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(DEFAULT_ROWS_PER_PAGE);
  const { organization } = contextStore.useState();

  const { data, isFetching } = useQuery(
    [
      "getLiveOnlineCourses",
      organization?.id,
      order,
      orderBy,
      page,
      rowsPerPage,
      filter,
    ],
    () =>
      catalogService.getLiveOnlineCourses(
        ListRequestOfCatalogFilter.fromJS({
          pageNumber: page,
          pageSize: rowsPerPage,
          sortField: orderBy,
          sortDescending: order === "desc",
          parameters: {
            ...filter,
            eventStartDate:
              filter.eventStartDate && filter.eventStartDate > new Date()
                ? filter.eventStartDate
                : new Date(),
          },
        })
      ),
    { keepPreviousData: true }
  );

  useEffect(() => {
    setPage(0);
  }, [filter]);

  const cells: Array<TableCellType> = [
    {
      accessor: "title",
      type: "node",
      label: t({ id: "title" }),
      View: ({ value }: { value: LiveOnlineCourseModel }) => (
        <CourseTitleCell skuId={value.id} title={value.title} />
      ),
    },
    {
      accessor: "releaseDate",
      type: "date",
      label: t({ id: "releaseDate" }),
    },
    {
      accessor: "credits",
      type: "node",
      label: t({ id: "credits" }),
      View: CreditsCell,
    },
    {
      accessor: "eventStartDate",
      type: "node",
      label: t({ id: "dateTime" }),
      View: DateTimeCell,
    },
    {
      accessor: "instructor",
      type: "text",
      label: t({ id: "instructor" }),
      noSort: true,
    },
    {
      accessor: "",
      type: "node",
      label: "",
      View: ({ value }: { value: LiveOnlineCourseModel }) => (
        <Link to={courseDetailsEnrollUsersUrl(value.id)}>
          <Button startIcon={<AddIcon />}>{t({ id: "register" })}</Button>
        </Link>
      ),
    },
  ];

  return (
    <LazyTable
      {...{
        "aria-label": t({ id: "liveOnlineCatalog" }),
        loading: isFetching,
        rowCount: data?.totalRecords ?? 0,
        rows: data?.data ?? [],
        titleComponent: viewSwitch,
        cells,
        page,
        setPage,
        order,
        setOrder,
        orderBy,
        setOrderBy,
        rowsPerPage,
        setRowsPerPage,
      }}
    />
  );
}
